<template>
  <div class="order-creation-loader-wrapper">
    <div class="order-creation-loader card-md">
      <div class="card-body">
        <IconLoader class="text-muted icon-rotate" stroke="1.2" :size="48" />
        <Transition name="loader-message-fade" mode="out-in">
          <div :key="currentIndex" class="loader-message">
            <h3 class="m-0">{{ currentTitle }}</h3>
            <p class="text-muted m-0">{{ currentSubtitle }}</p>
          </div>
        </Transition>
      </div>
      <div v-if="providers && providers.length" class="provider-icons">
        <component
          v-for="p in providers"
          :key="p.key"
          :is="p.icon"
          :size="32"
          :stroke-width="1.5"
          v-tooltip="p.label"
          :class="['provider-icon', { 'is-generated': p.generated, 'is-active': p.active }]"
          :style="p.generated ? { color: p.color } : undefined" />
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { IconLoader } from '@tabler/icons-vue';
import { computed, onBeforeUnmount, onMounted, ref, watch, type Component } from 'vue';

interface LoaderMessage {
  title: string;
  subtitle: string;
}

interface LoaderProvider {
  key: string;
  icon: Component;
  color: string;
  label: string;
  generated: boolean;
  active: boolean;
}

const props = withDefaults(
  defineProps<{
    title?: string;
    subtitle?: string;
    messages?: LoaderMessage[];
    intervalMs?: number;
    providers?: LoaderProvider[];
  }>(),
  {
    title: '',
    subtitle: '',
    messages: () => [],
    intervalMs: 4500,
    providers: () => [],
  }
);

const currentIndex = ref(0);
let timerId: ReturnType<typeof setInterval> | null = null;

const hasRotating = computed(() => props.messages.length > 0);

// the message set can be swapped for a shorter one mid-rotation (the loader switches from the
// banner stage's messages to the campaign stage's), so never index past the end
const currentMessage = computed(() => props.messages[currentIndex.value % (props.messages.length || 1)]);

const currentTitle = computed(() => (hasRotating.value ? currentMessage.value.title : props.title));
const currentSubtitle = computed(() => (hasRotating.value ? currentMessage.value.subtitle : props.subtitle));

function startRotation() {
  stopRotation();
  if (!hasRotating.value || props.messages.length < 2) return;
  timerId = setInterval(() => {
    currentIndex.value = (currentIndex.value + 1) % props.messages.length;
  }, props.intervalMs);
}

function stopRotation() {
  if (timerId) clearInterval(timerId);
  timerId = null;
}

onMounted(startRotation);
onBeforeUnmount(stopRotation);

watch(
  () => props.messages.length,
  () => {
    currentIndex.value = 0;
    startRotation();
  }
);
</script>
<style scoped>
.order-creation-loader-wrapper {
  display: grid;
  justify-items: center;
  align-items: center;
  height: 100%;
}

.order-creation-loader {
  width: 560px;
  min-height: 220px;
  background-color: white;
  border-radius: 10px;
  justify-self: center;
  align-self: center;
  overflow: hidden;
}

.order-creation-loader .card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

.loader-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
}

.provider-icons {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 0 0 20px;
}

.provider-icon {
  color: #d1d5db;
  transition: color 0.3s ease;
}

.provider-icon.is-active {
  animation: provider-pulse 1.2s ease-in-out infinite;
}

@keyframes provider-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.45;
    transform: scale(1.12);
  }
}

.loader-message-fade-enter-active,
.loader-message-fade-leave-active {
  transition:
    opacity 0.35s ease,
    transform 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.loader-message-fade-enter-from {
  opacity: 0;
  transform: translateX(40px);
}

.loader-message-fade-leave-to {
  opacity: 0;
  transform: translateX(-40px);
}
</style>
